import { useState } from "react";
import { motion } from "framer-motion";
import { useApp } from "@/contexts/AppContext";
import { ArrowRight, Sparkles, MessageSquare, Globe, ShieldCheck, Loader2, Star } from "lucide-react";
import logoFull from "@/assets/logo-full.jpeg";
import heroSocial from "@/assets/hero-social.jpg";
import LiveCounter from "@/components/LiveCounter";

const features = [
  { icon: Sparkles, label: "Instant match" },
  { icon: MessageSquare, label: "Real chats" },
  { icon: Globe, label: "Worldwide" },
  { icon: ShieldCheck, label: "Safe & moderated" },
];

const WelcomePage = () => {
  const { completeWelcome, loginAsGuest } = useApp();
  const [guestLoading, setGuestLoading] = useState(false);

  const handleGuest = async () => {
    if (guestLoading) return;
    setGuestLoading(true);
    try {
      completeWelcome();
      await loginAsGuest();
    } finally {
      setGuestLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-background overflow-y-auto relative" style={{ touchAction: "pan-y" }}>
      {/* Ambient brand glows */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-24 w-80 h-80 rounded-full bg-primary/[0.10] blur-[110px]" />
        <div className="absolute top-1/3 -right-24 w-80 h-80 rounded-full bg-accent/[0.10] blur-[110px]" />
      </div>

      <div className="relative z-10 min-h-[100dvh] flex flex-col px-6 pt-8 pb-7">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-center mb-5"
        >
          <img
            src={logoFull}
            alt="TalkLot"
            className="h-12 object-contain drop-shadow-2xl"
            style={{ mixBlendMode: "screen" }}
          />
        </motion.div>

        {/* Hero card with image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="relative rounded-3xl overflow-hidden border border-border/30 shadow-2xl mb-6"
        >
          <img
            src={heroSocial}
            alt="People chatting and laughing together"
            width={1024}
            height={1024}
            className="w-full h-72 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-background/10" />

          {/* Floating live badge */}
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-background/70 backdrop-blur-md border border-border/40 flex items-center gap-1.5"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
            <span className="text-[10px] font-semibold text-foreground">LIVE NOW</span>
          </motion.div>

          {/* Bottom overlay text */}
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-[28px] leading-tight font-display font-bold text-foreground mb-2"
            >
              Meet someone <span className="text-gradient-premium">new</span> in seconds
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28 }}
              className="text-muted-foreground text-sm leading-relaxed max-w-xs"
            >
              Real text chats with real people — fast, safe, and effortless.
            </motion.p>
          </div>
        </motion.div>

        {/* Feature chips */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="grid grid-cols-2 gap-2 mb-5"
        >
          {features.map((f) => (
            <div
              key={f.label}
              className="rounded-xl bg-muted/20 border border-border/15 px-3 py-2.5 flex items-center gap-2"
            >
              <div className="w-7 h-7 rounded-lg gradient-premium flex items-center justify-center flex-shrink-0">
                <f.icon className="w-3.5 h-3.5 text-primary-foreground" />
              </div>
              <span className="text-xs font-display font-semibold text-foreground">{f.label}</span>
            </div>
          ))}
        </motion.div>

        {/* Social proof */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex items-center justify-center gap-3 mb-5"
        >
          <LiveCounter />
          <div className="h-3 w-px bg-border/40" />
          <div className="flex items-center gap-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} className="w-3 h-3 fill-primary text-primary" />
            ))}
            <span className="text-[11px] text-muted-foreground ml-1">4.8</span>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="mt-auto space-y-2.5"
        >
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={completeWelcome}
            className="w-full py-4 rounded-2xl gradient-premium text-primary-foreground font-display font-bold text-base flex items-center justify-center gap-2 shadow-lg shadow-primary/20"
          >
            Start Chatting <ArrowRight className="w-4 h-4" />
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={handleGuest}
            disabled={guestLoading}
            className="w-full py-3.5 rounded-2xl bg-muted/30 border border-border/30 text-foreground font-display font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {guestLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue as Guest"}
          </motion.button>
          <p className="text-center text-[11px] text-muted-foreground/70 pt-1">
            Free to use · No credit card required
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default WelcomePage;